import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Container, Title, Text, Button, Paper, Group, Stack, Modal,
  TextInput, Textarea, Badge, Loader, ActionIcon,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { Plus, Users, ChevronRight } from 'lucide-react'
import { rosterApi, Roster } from '../../api/roster'

export function RosterList() {
  const navigate = useNavigate()
  const [rosters, setRosters] = useState<Roster[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  const form = useForm({
    initialValues: { name: '', description: '' },
    validate: { name: (v) => (v.trim() ? null : 'Name is required') },
  })

  const load = () => {
    rosterApi.list()
      .then((res) => setRosters(res.data))
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load rosters', color: 'red' }))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleCreate = async (values: typeof form.values) => {
    setCreating(true)
    try {
      await rosterApi.create({ name: values.name, description: values.description || undefined })
      notifications.show({ title: 'Created', message: `Roster "${values.name}" created.`, color: 'green' })
      form.reset()
      setModalOpen(false)
      load()
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed to create roster', color: 'red' })
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  return (
    <Container size="md" py={32}>
      <Group justify="space-between" mb={24}>
        <Box>
          <Title order={2} c="var(--sat-text)">My Rosters</Title>
          <Text size="sm" c="var(--sat-text-soft)" mt={2}>Manage your student groups.</Text>
        </Box>
        <Button
          leftSection={<Plus size={16} />}
          color="grapefruit"
          onClick={() => setModalOpen(true)}
        >
          New Roster
        </Button>
      </Group>

      {rosters.length === 0 ? (
        <Paper shadow="xs" radius="md" p="xl" style={{ textAlign: 'center', background: 'var(--sat-bg)' }}>
          <Users size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
          <Text fw={600} c="var(--sat-text)" mb={4}>No rosters yet</Text>
          <Text size="sm" c="var(--sat-text-soft)" mb={16}>Create a roster and add students to get started.</Text>
          <Button color="grapefruit" onClick={() => setModalOpen(true)}>
            Create First Roster
          </Button>
        </Paper>
      ) : (
        <Stack gap={12}>
          {rosters.map((roster) => (
            <Paper
              key={roster.id}
              shadow="xs"
              radius="md"
              p="lg"
              style={{ cursor: 'pointer', border: '1px solid var(--sat-border-soft)', transition: 'box-shadow 0.15s' }}
              onClick={() => navigate(`/tutor/rosters/${roster.id}`)}
            >
              <Group justify="space-between">
                <Group gap={12}>
                  <Box
                    style={{
                      width: 44, height: 44, borderRadius: 10,
                      background: 'var(--sat-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Users size={22} color="var(--sat-red)" />
                  </Box>
                  <Box>
                    <Text fw={600} c="var(--sat-text)">{roster.name}</Text>
                    {roster.description && <Text size="xs" c="var(--sat-text-soft)">{roster.description}</Text>}
                    <Text size="xs" c="var(--sat-text-faint)" mt={2}>
                      Created {roster.create_datetime ? new Date(roster.create_datetime).toLocaleDateString() : '—'}
                    </Text>
                  </Box>
                </Group>
                <Group gap={8}>
                  <Badge color={roster.is_active ? 'green' : 'gray'} variant="light">
                    {roster.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <ActionIcon variant="subtle" color="gray">
                    <ChevronRight size={18} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); form.reset() }}
        title={<Text fw={700} c="var(--sat-text)">New Roster</Text>}
        radius="md"
      >
        <form onSubmit={form.onSubmit(handleCreate)}>
          <Stack gap={16}>
            <TextInput
              label="Roster Name"
              placeholder="e.g. Sprint 2026 SAT Prep"
              required
              {...form.getInputProps('name')}
            />
            <Textarea
              label="Description (optional)"
              placeholder="Brief description of this class group"
              rows={3}
              {...form.getInputProps('description')}
            />
            <Group justify="flex-end" mt={8}>
              <Button variant="subtle" color="gray" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="submit" color="grapefruit" loading={creating}>
                Create Roster
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  )
}
